/**
 * Analytics configuration.
 *
 * Reads the GA4 + Source Digital pixel settings from the environment. Both
 * sinks are OFF unless their env vars are present, which is what makes
 * analytics production-only: the vars are injected in `ci-production.yml`
 * only, so dev/staging builds have no IDs and every call becomes a no-op.
 *
 * To verify locally, put the same vars in a `.env.local` (git-ignored).
 */

const bool = (value, fallback) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return String(value).toLowerCase() !== 'false';
};

// GA4 — reuses the existing "Watch Site" measurement ID so history stays
// continuous with watch.bkfc.com.
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

// Source Digital pixel (@source-digital/pixel) — first-party-style beacon that
// survives most ad blockers. `id` is the property (constant across BKFC apps);
// `platform`/`app` params carry the per-device dimension so Redash can roll up
// all of BKFC by `id=bkfc` and slice by platform.
export const PIXEL_BEACON_URL = process.env.REACT_APP_PIXEL_BEACON_URL || '';
export const PIXEL_ID = process.env.REACT_APP_PIXEL_ID || 'bkfc';
// 'image' (GET) — the beacon endpoint is a GET-only GCS object; POST 400s.
export const PIXEL_TRANSPORT = process.env.REACT_APP_PIXEL_TRANSPORT || 'image';
export const PIXEL_USER_ENABLED = bool(process.env.REACT_APP_PIXEL_ENABLED, true);

// BKFC's SourceSync organization id (the `oid` param). Sent on every event so
// bkfc-web data joins the rest of BKFC's SourceSync footprint (experiences,
// distributions) under one org in Redash. Optional until SourceSync confirms
// the production oid.
export const PIXEL_OID = process.env.REACT_APP_PIXEL_OID || '';

export const PLATFORM = 'web';
export const APP = 'bkfc-web';

// A stable-ish version string sent on every pixel event (avoids relying on the
// package's Vite-injected default). Falls back to the app version if present.
export const ANALYTICS_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

// Fraction of sessions that emit the periodic `video_heartbeat` liveness ping
// (deterministic per session_id). The escape hatch for heartbeat volume at
// scale: keep at 1.0 for exact concurrency; drop to e.g. 0.25 and weight counts
// ×4 if the firehose bites. Watch-time/QoE KPIs live on the per-playback
// summary + discrete QoE events, so they stay exact regardless of this dial.
const clamp01 = n => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 1);
export const HEARTBEAT_SAMPLE_RATE = clamp01(
  parseFloat(process.env.REACT_APP_HEARTBEAT_SAMPLE_RATE),
);

/** GA4 sink is enabled only when a measurement ID is configured. */
export const gaEnabled = () => Boolean(GA_MEASUREMENT_ID);

/** Pixel sink is enabled only when the beacon URL + id are configured. */
export const pixelEnabled = () =>
  PIXEL_USER_ENABLED && Boolean(PIXEL_BEACON_URL) && Boolean(PIXEL_ID);

/** Any sink enabled? */
export const analyticsEnabled = () => gaEnabled() || pixelEnabled();

/**
 * Console logging for local verification. On in non-production, or when a
 * developer sets `analytics_logging_enabled=true` in localStorage.
 */
export const consoleLoggingEnabled = () => {
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }
  try {
    return window.localStorage.getItem('analytics_logging_enabled') === 'true';
  } catch (e) {
    return false;
  }
};
